"use client";

import React, { createContext, useContext, useState, useEffect } from "react";
import type { User, GatewayView } from "@/lib/types";

interface GatewayContextValue {
  currentUser: User | null;
  setCurrentUser: (user: User | null) => void;
  view: GatewayView;
  setView: (view: GatewayView) => void;
  isInternal: boolean;
  isClient: boolean;
  isTalent: boolean;
  companyId: string | null; // For client view, which company they belong to
  isLoading: boolean;
  signOut: () => Promise<void>;
}

const GatewayContext = createContext<GatewayContextValue | undefined>(undefined);

// Visible phases for each view type (Standard Kanban)
export const VISIBLE_PHASES = {
  internal: [
    "request",      // New service requests
    "backlog",      // Approved work waiting to be scheduled
    "blocked",      // Work blocked by dependencies
    "todo",         // Ready to start
    "in_progress",  // Actively being worked on
    "review",       // Awaiting approval
    "done",         // Completed and delivered
    "archived",     // Historical records
  ],
  customer: [
    "request",      // Shows as "Your Requests"
    "backlog",      // Shows as "Scheduled"
    "blocked",      // Shows as "On Hold"
    "todo",         // Shows as "Upcoming"
    "in_progress",  // Shows as "In Progress"
    "review",       // Shows as "Review"
    "done",         // Shows as "Delivered"
    // archived is HIDDEN from customers
  ],
  talent: [] as const, // Talent users don't see project phases - they only see their application status
} as const;

// Phase label overrides for customer view
export const CUSTOMER_PHASE_LABELS: Record<string, string> = {
  request: "Your Requests",
  backlog: "Scheduled",
  blocked: "On Hold",
  todo: "Upcoming",
  in_progress: "In Progress",
  review: "Review",
  done: "Delivered",
};

// Function to get customer phase labels with translations
export function getCustomerPhaseLabels(t: {
  yourRequests: string;
  scheduled: string;
  onHold: string;
  upcoming: string;
  inProgress: string;
  review: string;
  delivered: string;
}): Record<string, string> {
  return {
    request: t.yourRequests,
    backlog: t.scheduled,
    blocked: t.onHold,
    todo: t.upcoming,
    in_progress: t.inProgress,
    review: t.review,
    done: t.delivered,
  };
}

interface GatewayProviderProps {
  children: React.ReactNode;
  initialUser?: User | null;
  initialLoading?: boolean;
  onSignOut?: () => Promise<void>;
}

export function GatewayProvider({
  children,
  initialUser = null,
  initialLoading = false,
  onSignOut,
}: GatewayProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(initialUser);
  const [view, setView] = useState<GatewayView>("internal");
  const [isLoading, setIsLoading] = useState(initialLoading);

  // Sync currentUser with initialUser prop (user is loaded async in Providers)
  useEffect(() => {
    setCurrentUser(initialUser);
  }, [initialUser]);

  // Update loading state when it changes from parent
  useEffect(() => {
    setIsLoading(initialLoading);
  }, [initialLoading]);

  // Determine view based on user role
  useEffect(() => {
    if (currentUser) {
      if (currentUser.role === "customer") {
        setView("customer");
      } else if (currentUser.role === "talent") {
        setView("talent");
      } else {
        setView("internal");
      }
    } else {
      // Reset to internal view when user logs out
      setView("internal");
    }
  }, [currentUser]);

  const isInternal = view === "internal";
  const isClient = view === "customer";
  const isTalent = view === "talent";
  const companyId = currentUser?.company_id || null;

  const signOut = async () => {
    if (onSignOut) {
      await onSignOut();
    }
    setCurrentUser(null);
    setView("internal");
  };

  return (
    <GatewayContext.Provider
      value={{
        currentUser,
        setCurrentUser,
        view,
        setView,
        isInternal,
        isClient,
        isTalent,
        companyId,
        isLoading,
        signOut,
      }}
    >
      {children}
    </GatewayContext.Provider>
  );
}

export function useGateway() {
  const context = useContext(GatewayContext);
  if (context === undefined) {
    throw new Error("useGateway must be used within a GatewayProvider");
  }
  return context;
}

// Helper hook to get visible phases based on current view
export function useVisiblePhases() {
  const { view } = useGateway();
  return VISIBLE_PHASES[view];
}

// Helper hook to get phase label based on view
export function usePhaseLabel(phaseId: string) {
  const { view } = useGateway();
  if (view === "customer" && CUSTOMER_PHASE_LABELS[phaseId]) {
    return CUSTOMER_PHASE_LABELS[phaseId];
  }
  return null; // Use default label
}
